
import { useTranslation, UseTranslationResponse } from 'react-i18next';
import { useMemo } from 'react';
import { TranslationText } from '@/components/i18n/TranslationText';

// Define possible types for the second parameter of t function
type TranslationValues = Record<string, string | number>;
type FallbackOrValues = string | TranslationValues | undefined;

// Helper function to determine if the second parameter is a string fallback or interpolation values
function isFallback(value: FallbackOrValues): value is string {
  return typeof value === 'string';
}

interface EnhancedTranslationResponse {
  t: (key: string, fallbackOrValues?: FallbackOrValues) => string;
  i18n: UseTranslationResponse<"translation", undefined>["i18n"];
  ready: boolean;
  tElement: (key: string, fallbackOrValues?: FallbackOrValues) => JSX.Element;
}

export function useEnhancedTranslation(): EnhancedTranslationResponse {
  const translation = useTranslation();
  
  const t = useMemo(
    () => (key: string, fallbackOrValues?: FallbackOrValues) => {
      // Handle the case where fallbackOrValues is a string (fallback)
      if (isFallback(fallbackOrValues)) {
        const translatedText = translation.t(key, { defaultValue: fallbackOrValues });
        
        // If translation returns the key itself and we have a fallback, use the fallback
        if (translatedText === key && fallbackOrValues) {
          return fallbackOrValues;
        }
        
        return translatedText;
      }
      
      // Handle the case where fallbackOrValues is an object (interpolation values)
      return translation.t(key, fallbackOrValues);
    },
    [translation.t]
  );
  
  const tElement = useMemo(
    () => (key: string, fallbackOrValues?: FallbackOrValues) => {
      const translatedText = String(t(key, fallbackOrValues));
      return (
        <TranslationText i18nKey={key}>
          {translatedText}
        </TranslationText>
      );
    },
    [t]
  );
  
  return {
    t,
    tElement,
    i18n: translation.i18n,
    ready: translation.ready
  };
}
